import { EntityAudit } from "@modernary/mui/components/audit";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocumentsChildList,
  useEntityDocumentStore,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { getMurnString } from "@modernary/mui/utils/helpers";
import { onUnmounted } from "vue";
import { RouterView, useRoute, type RouterOptions } from "vue-router";
import { providePermitVersionDocumentContext } from "./permitVersionDocumentRoute";

const names = {
  dustPermits: "DustPermit.DustPermits",
  list: "DustPermit.DustPermitList",
  base: "DustPermit.DustPermitBase",
  view: "DustPermit.DustPermitView",
  new: "DustPermit.DustPermitNew",
  edit: "DustPermit.DustPermitEdit",
  notes: "DustPermit.DustPermitNotes",
  noteEdit: "DustPermit.DustPermitNoteEdit",
  noteNew: "DustPermit.DustPermitNoteNew",
  documents: "DustPermit.DustPermitDocuments",
  documentEdit: "DustPermit.DustPermitDocumentsEdit",
  documentNew: "DustPermit.DustPermitDocumentsNew",
  audit: "DustPermit.DustPermitAudit",
  versions: "DustPermit.DustPermitVersions",
  affiliations: "DustPermit.DustPermitAffiliations",
  versionAffiliations: "DustPermit.DustPermitVersionAffiliations",
  versionNew: "DustPermit.DustPermitVersionNew",
  versionEdit: "DustPermit.DustPermitVersionEdit",
  modifyRenewPermitVersion: "DustPermit.DustPermitModifyRenewPermitVersion",
};

export { names as dustPermitRouteNames };

const dustPermitRoutes: RouterOptions["routes"] = [
  {
    path: "/dustPermit",
    name: names.dustPermits,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: { title: "Dust Permits", lookupGroups: ["PermitStatus"] },
        component: () => import("@/views/dustPermit/DustPermitList.vue"),
      },
      {
        path: "new/:facilityId(\\d+)?",
        component: () => import("@/views/dustPermit/DustPermitBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "Permit New", isNew: true },
            component: () => import("@/views/dustPermit/DustPermitEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)/:versionId(\\d+)",
        name: names.base,
        component: () => import("@/views/dustPermit/DustPermitBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Permit View" },
            component: () => import("@/views/dustPermit/DustPermitView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Permit Edit", isForm: true },
            component: () => import("@/views/dustPermit/DustPermitEdit.vue"),
          },
          {
            path: "affiliations",
            name: names.affiliations,
            meta: { title: "Current Permit Affiliations" },
            component: () => import("@/views/dustPermit/DustPermitVersionAffiliations.vue"),
          },
          // {
          //   path: "versions/:versionId(\\d+)/affiliations",
          //   name: names.versionAffiliations,
          //   meta: { title: "Permit Affiliations" },
          //   component: () => import("@/views/dustPermit/DustPermitVersionAffiliations.vue"),
          // },
          // {
          //   path: "versions/:versionId(\\d+)",
          //   name: names.versionEdit,
          //   meta: { title: "Edit Permit", isForm: true },
          //   component: () => import("@/views/dustPermit/DustPermitVersionEdit.vue"),
          // },
          // {
          //   path: "versions/new",
          //   name: names.versionNew,
          //   meta: { title: "Create Permit", isForm: true },
          //   component: () => import("@/views/dustPermit/DustPermitVersionEdit.vue"),
          // },
          // {
          //   path: "modifyVersion/:versionId(\\d+)",
          //   name: names.modifyRenewPermitVersion,
          //   meta: { title: "Modify Permit", isForm: true },
          //   component: () => import("@/views/dustPermit/ModifyRenewDustPermitVersion.vue"),
          // },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Permit Notes" },
            component: {
              render: () => <EntityNotes idField="permitId" routeNamePrefix="DustPermit.DustPermitNote" />,
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Permit Note Edit", isForm: true },
            component: () => import("@/views/dustPermit/DustPermitNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Permit Note New", isForm: true },
            component: () => import("@/views/dustPermit/DustPermitNote.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Permit Documents" },
            component: {
              setup() {
                providePermitVersionDocumentContext();
                return () => <EntityDocumentsChildList resourceTypeKey="permit" />;
              },
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentEdit,
            meta: { title: "Edit Permit Document", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="permit" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentNew,
            meta: { title: "Create Permit Document", isForm: true },
            component: {
              setup() {
                const route = useRoute();
                const entityDocumentStore = useEntityDocumentStore();
                const versionMurn = getMurnString(
                  "permit",
                  `${String(route.params.id)}/version/${String(route.params.versionId)}`,
                );
                entityDocumentStore.relatedEntities = [{ relatedEntityMurn: versionMurn }];
                onUnmounted(() => {
                  entityDocumentStore.relatedEntities = [];
                });
                return () => <EntityDocumentMultipleForm resourceTypeKey="permit" />;
              },
            },
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Permit Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="Permit" />,
            },
          },
        ],
      },
    ],
  },
];
export default dustPermitRoutes;
